import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Image, Modal, ScrollView} from "react-native";
import {useNavigation} from "@react-navigation/native";
import Div from "../lib/Div";
import evenplayLogo from "../assets/img/evenplay-logo-long.svg";
import {PaymentType} from "../types";
import {StoreContext} from "../components/StoreContextProvider";
import {colors, layout} from "./withdraw-flow/theme";
import {WithdrawReceiptSnapshot} from "./withdraw-flow/types";
import {usePayoutMethods} from "./withdraw-flow/usePayoutMethods";
import {usePayoutVerification} from "./withdraw-flow/usePayoutVerification";
import {useFinixCardEntry} from "./withdraw-flow/useFinixCardEntry";
import {usePaysafeCardEntry} from "./withdraw-flow/usePaysafeCardEntry";
import {useSavedCardManagement} from "./withdraw-flow/useSavedCardManagement";
import {useWithdrawFlowNavigation} from "./withdraw-flow/useWithdrawFlowNavigation";
import {FlowHeader, flowStyles} from "./withdraw-flow/WithdrawFlowPrimitives";
import {
  WithdrawCardEntryStep, WithdrawDestinationStep, WithdrawFinixCardEntryStep, WithdrawMethodStep, WithdrawOtpStep,
  WithdrawPaysafeCardEntryStep, WithdrawReviewStep
} from "./withdraw-flow/WithdrawFlowSteps";
import {WithdrawResultScreen} from "./withdraw-flow/WithdrawResultScreens";
import {buildAccountHolderName} from "../screens/User/Account/Tabs/Wallet/AddCardInfoModal/helpers";
import {buildPayoutDestinationLabel} from "./withdraw-flow/payoutMethodsPolicy";
import {isFullSwing} from "../constants";

type Props = { amount: string, onClose: () => void };

const WithdrawFlowModal = ({amount, onClose}: Props) => {
  const navigation: any = useNavigation();
  const {getUser, user} = useContext(StoreContext);
  const amountValue = Number(amount) || 0;
  const amountCents = Math.round(amountValue * 100);
  const {
    methodRows, availableMethodRows, isLoadingPayoffMethods, payoffMethodsError, cardEntryProvider,
    clearCardEntryProvider, resetPayoffMethods, loadPayoffMethods: loadPayoutMethodsResource
  } = usePayoutMethods(amountCents);
  const [methodType, setMethodType] = useState<PaymentType>();
  const {
    step, currentStep, getTitle: getStepTitle, reset: resetNavigation, showMethod, showDestination, showReview, showOtp,
    goBack: navigateBack
  } = useWithdrawFlowNavigation();
  const {
    cardDestinations, selectedDestination, destinationId, canAddCard, canContinueFromDestinationStep,
    isLoadingSavedCards, hasLoadedSavedCards, savedCardsError, isAddingCard, saveCardFormError, isSavingCard,
    requiresSavedCardsCheck, tokenizationStatus, tokenizationError, cardNumberInputProps, expiryInputProps,
    cvcInputProps, cancelSavedCardsLoad, resetSavedCardFlow, loadSavedCards, startAddCard, cancelAddCard,
    checkSavedCardsAfterUnknownSave, submitSaveCard, retryCardTokenization, handleMethodSelected, selectDestination,
    registerFinixDestination, registerPaysafeDestination
  } = useSavedCardManagement({
    amount, userId: user?.userId?.toString() || '', methodType, isDestinationStepActive: step === 'destination',
    cardEntryProvider
  });

  const isAddingCardOnDestinationStep = step === 'destination' && isAddingCard;

  // SMG-4057 (D6). Paysafe entry is server-driven: only when the payoff-methods response says
  // `cardEntryProvider: 'PAYSAFE'` AND this build carries the Paysafe public key. Otherwise — the
  // field absent, as it is for everyone by default — the Finix / eProtect choice below is untouched.
  // A card save the server refuses for region / rollout clears the field, handing entry back to them.
  const paysafeCardEntry = usePaysafeCardEntry(
    isAddingCardOnDestinationStep && cardEntryProvider === 'PAYSAFE', clearCardEntryProvider
  );
  const isPaysafeCardEntry = cardEntryProvider === 'PAYSAFE' && paysafeCardEntry.isSupported;

  // SMG-4557. Only mounts where FINIX_APPLICATION_ID + FINIX_ENVIRONMENT are both set, so every
  // environment without them keeps the existing eProtect entry with no behaviour change.
  const finixCardEntry = useFinixCardEntry(isAddingCardOnDestinationStep && !isPaysafeCardEntry);
  const isCardEntrySaving = finixCardEntry.isSaving || paysafeCardEntry.isSaving;

  /**
   * Save a Finix-tokenized card and make it the selected payout destination.
   *
   * <p>Registering the returned `finix:<id>` handle is what closes the loop. A Finix instrument is
   * not a `SavedCard`, so reloading `getSavedCards()` would return the same list it did before and
   * leave a user who has just added a card looking at "No saved payout cards yet" with Continue
   * disabled. The handle travels to the payout as `cardUid`, where paygate's `resolvePayoffCard`
   * accepts the `finix:` namespace and re-validates ownership server-side (SMG-4557).</p>
   */
  const submitFinixCard = async () => {
    const instrument = await finixCardEntry.saveCard(user?.phoneNumber || '');
    if (!instrument?.destinationRef) return;
    registerFinixDestination(instrument.destinationRef, instrument.lastFour);
  };

  /**
   * Save a Paysafe-tokenized card and select its `paysafe:<id>` handle — same loop as Finix above.
   * The profile's address goes along with the tokenize: Paysafe keeps it on the saved card, and a US
   * payout to that card needs street, city and state as well as country + zip.
   */
  const submitPaysafeCard = async () => {
    const instrument = await paysafeCardEntry.saveCard({
      street: user?.address, city: user?.city, state: user?.state, country: user?.country, zip: user?.zipcode
    });
    if (!instrument?.destinationRef) return;
    registerPaysafeDestination(instrument.destinationRef, instrument.lastFour);
  };

  const confirmationEmail = user?.emailAddress || '';
  const accountHolderName = buildAccountHolderName(user);
  const selectedMethod = availableMethodRows.find(method => method.type === methodType);
  const hasAlternativeMethod = availableMethodRows.some(method => method.type !== methodType);
  const currentReceiptSnapshot = useMemo<WithdrawReceiptSnapshot | undefined>(() => {
    if (!selectedMethod) return undefined;

    return {
      amountValue, destinationLabel: buildPayoutDestinationLabel(selectedMethod.type, selectedDestination),
      methodType: selectedMethod.type, methodLabel: selectedMethod.reviewTitle, feeLabel: selectedMethod.feeLabel,
      netAmountLabel: selectedMethod.netAmountLabel
    };
  }, [
    amountValue, selectedDestination?.summary, selectedMethod?.feeLabel, selectedMethod?.netAmountLabel,
    selectedMethod?.reviewTitle, selectedMethod?.type
  ]);

  const {
    isSubmittingWithdrawal, withdrawResult, receiptSnapshot, isStartingVerification, payoutVerification, otpCode,
    otpError, verificationStartError, verificationRestartRequired, isResendingVerification, resendAvailableInSeconds,
    setOtpCode, clearOtpError, clearWithdrawResult, resetPayoutVerification, resetPayoutFlow,
    submitWithdrawal: submitWithdrawalRequest, startPayoutVerification: startPayoutVerificationRequest,
    resendPayoutVerification: resendPayoutVerificationRequest,
    restartPayoutVerification: restartPayoutVerificationRequest
  } = usePayoutVerification({
    isActive: !!amount, userId: user?.userId?.toString() || '', amountCents, methodType, selectedDestination,
    currentReceiptSnapshot, refreshUser: getUser
  });

  const closeFlow = useCallback(() => {
    if (isSavingCard || isSubmittingWithdrawal || isCardEntrySaving) return;

    resetPayoffMethods();
    resetSavedCardFlow();
    resetPayoutFlow();
    resetNavigation();
    onClose();
  }, [
    isCardEntrySaving, isSavingCard, isSubmittingWithdrawal, onClose, resetNavigation, resetPayoffMethods,
    resetPayoutFlow, resetSavedCardFlow
  ]);

  const stepTitle = getStepTitle(isAddingCard);

  const loadPayoffMethods = useCallback(async () => {
    if (!amount) return;

    const result = await loadPayoutMethodsResource();
    if (result.status === 'stale') return;

    if (result.status === 'success') {
      cancelSavedCardsLoad();
    }

    resetPayoutVerification();
    if (result.status === 'success' && result.nextMethodType) {
      setMethodType(result.nextMethodType);
      return;
    }

    setMethodType(undefined);
  }, [amount, cancelSavedCardsLoad, loadPayoutMethodsResource, resetPayoutVerification]);

  useEffect(() => {
    resetPayoffMethods();
    resetSavedCardFlow();
    resetPayoutFlow();
    resetNavigation();

    if (!amount) return;

    setMethodType(undefined);
    void loadPayoffMethods();
  }, [
    amount, loadPayoffMethods, resetNavigation, resetPayoffMethods, resetPayoutFlow, resetSavedCardFlow
  ]);

  const goBack = useCallback(() => {
    navigateBack({
      isAddingCard, isBusy: isSavingCard || isSubmittingWithdrawal || isCardEntrySaving,
      onCancelAddCard: cancelAddCard,
      onClose: closeFlow, onLeaveReview: resetPayoutVerification, onLeaveOtp: clearOtpError
    });
  }, [
    cancelAddCard, clearOtpError, closeFlow, isAddingCard, isCardEntrySaving, isSavingCard,
    isSubmittingWithdrawal, navigateBack, resetPayoutVerification
  ]);

  const selectMethod = useCallback((nextMethodType: PaymentType) => {
    if (!availableMethodRows.some(method => method.type === nextMethodType)) return;

    handleMethodSelected(nextMethodType);
    setMethodType(nextMethodType);
  }, [availableMethodRows, handleMethodSelected]);

  const continueFromMethodStep = useCallback(() => {
    if (!selectedMethod) return;
    showDestination();
  }, [selectedMethod, showDestination]);

  const viewTransactionHistory = useCallback(() => {
    closeFlow();
    navigation.navigate('TransactionHistory');
  }, [closeFlow, navigation]);

  const startPayoutVerification = useCallback(() => {
    void startPayoutVerificationRequest().then(isReady => {
      if (isReady) showOtp();
    });
  }, [showOtp, startPayoutVerificationRequest]);

  const resendPayoutVerification = useCallback(() => {
    void resendPayoutVerificationRequest();
  }, [resendPayoutVerificationRequest]);

  const restartPayoutVerification = useCallback(() => {
    void restartPayoutVerificationRequest().then(isReady => {
      if (!isReady) showReview();
    });
  }, [restartPayoutVerificationRequest, showReview]);

  const submitWithdrawal = useCallback(() => {
    void submitWithdrawalRequest().then(result => {
      if (result === 'method-required') showMethod();
    });
  }, [showMethod, submitWithdrawalRequest]);

  const destinationSummary = buildPayoutDestinationLabel(methodType, selectedDestination);
  const resultReceiptSnapshot = withdrawResult?.receiptSnapshot || receiptSnapshot || currentReceiptSnapshot;

  return (
    <Modal animationType="slide" visible={!!amount} onRequestClose={closeFlow}>
      <Div flex={1} alignItems="center" bg={withdrawResult ? colors.signatureBg : colors.bg}>
        <Div flex={1} w="100%" maxW={withdrawResult ? undefined : layout.mobileMax}
             bg={withdrawResult ? colors.signatureBg : colors.bg}>
          {withdrawResult && resultReceiptSnapshot ? (
            <>
              {!isFullSwing && (
                <Div h={56} row alignItems="center" px={12}>
                  <Div w={56}/>
                  <Div flex={1} alignItems="center">
                    <Image source={evenplayLogo as any} style={flowStyles.resultBrandLogo}/>
                  </Div>
                  <Div w={56}/>
                </Div>
              )}

              <WithdrawResultScreen
                result={withdrawResult} amountValue={resultReceiptSnapshot.amountValue}
                destinationLabel={resultReceiptSnapshot.destinationLabel}
                methodLabel={resultReceiptSnapshot.methodLabel} feeLabel={resultReceiptSnapshot.feeLabel}
                netAmountLabel={resultReceiptSnapshot.netAmountLabel} onDone={closeFlow} onRetry={clearWithdrawResult}
                onViewTransaction={viewTransactionHistory}
              />
            </>
          ) : (
            <>
              <FlowHeader title={stepTitle} currentStep={currentStep} onBack={goBack} onClose={closeFlow}/>

              <ScrollView style={flowStyles.scroll} contentContainerStyle={flowStyles.scrollContent}
                          showsVerticalScrollIndicator={false}>
                {step === 'method' && (
                  <WithdrawMethodStep
                    amountValue={amountValue} methodRows={methodRows} methodType={methodType}
                    isLoadingPayoffMethods={isLoadingPayoffMethods} payoffMethodsError={payoffMethodsError}
                    canContinue={!!selectedMethod} onRetry={loadPayoffMethods}
                    onSelectMethod={selectMethod} onContinue={continueFromMethodStep}
                  />
                )}

                {isAddingCardOnDestinationStep && isPaysafeCardEntry && (
                  <WithdrawPaysafeCardEntryStep
                    fieldHostIds={paysafeCardEntry.fieldHostIds} status={paysafeCardEntry.status}
                    errorMessage={paysafeCardEntry.errorMessage} formError={paysafeCardEntry.formError}
                    isSaving={paysafeCardEntry.isSaving} onSubmit={submitPaysafeCard}
                    onCancel={cancelAddCard} onRetry={paysafeCardEntry.retry}
                  />
                )}

                {isAddingCardOnDestinationStep && !isPaysafeCardEntry && finixCardEntry.isSupported && (
                  <WithdrawFinixCardEntryStep
                    mountElementId={finixCardEntry.mountElementId} status={finixCardEntry.status}
                    errorMessage={finixCardEntry.errorMessage} formError={finixCardEntry.formError}
                    isSaving={finixCardEntry.isSaving} onSubmit={submitFinixCard}
                    onCancel={cancelAddCard} onRetry={finixCardEntry.retry}
                  />
                )}

                {isAddingCardOnDestinationStep && !isPaysafeCardEntry && !finixCardEntry.isSupported && (
                  <WithdrawCardEntryStep
                    accountHolderName={accountHolderName} cardNumberInputProps={cardNumberInputProps}
                    expiryInputProps={expiryInputProps} cvcInputProps={cvcInputProps}
                    formError={saveCardFormError} isSavingCard={isSavingCard}
                    tokenizationStatus={tokenizationStatus} tokenizationError={tokenizationError}
                    requiresSavedCardsCheck={requiresSavedCardsCheck} onSubmit={submitSaveCard} onCancel={cancelAddCard}
                    onCheckSavedCards={checkSavedCardsAfterUnknownSave} onRetryTokenization={retryCardTokenization}
                  />
                )}

                {step === 'destination' && !isAddingCard && methodType && (
                  <WithdrawDestinationStep
                    methodType={methodType} cardDestinations={cardDestinations} destinationId={destinationId}
                    isLoadingSavedCards={isLoadingSavedCards} hasLoadedSavedCards={hasLoadedSavedCards}
                    savedCardsError={savedCardsError} onContinue={showReview} canAddCard={canAddCard}
                    canContinue={canContinueFromDestinationStep} hasAlternativeMethod={hasAlternativeMethod}
                    onRetry={loadSavedCards} onSelectDestination={selectDestination} onAddCard={startAddCard}
                    onChooseAnotherMethod={showMethod} onBackToWallet={closeFlow}
                  />
                )}

                {step === 'review' && selectedMethod && (
                  <WithdrawReviewStep
                    amountValue={amountValue} selectedMethod={selectedMethod}
                    selectedDestinationSummary={destinationSummary} confirmationEmail={confirmationEmail}
                    isSubmittingWithdrawal={isStartingVerification} submitError={verificationStartError}
                    submitLabel="Sending Code..." onSubmit={startPayoutVerification} onCancel={closeFlow}
                  />
                )}

                {step === 'otp' && (
                  <WithdrawOtpStep
                    amountValue={amountValue} verification={payoutVerification} otpCode={otpCode} formError={otpError}
                    isSubmittingWithdrawal={isSubmittingWithdrawal} isStartingVerification={isStartingVerification}
                    isResendingVerification={isResendingVerification}
                    verificationRestartRequired={verificationRestartRequired}
                    resendAvailableInSeconds={resendAvailableInSeconds} onChangeOtpCode={setOtpCode}
                    onSubmit={submitWithdrawal} onResend={resendPayoutVerification}
                    onRequestNewCode={restartPayoutVerification}
                  />
                )}
              </ScrollView>
            </>
          )}
        </Div>

      </Div>
    </Modal>
  );
};

export default React.memo(WithdrawFlowModal);
